import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FaAmazon, FaCheckCircle, FaTimesCircle, FaSpinner } from "react-icons/fa";
import { HiOutlineExternalLink } from "react-icons/hi";
import unauthenticatedAxios from "../redux/actions/AxiosInstance/unAuthenticated";

const AmazonCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState("authenticating");
  const [message, setMessage] = useState("Processing Amazon authorization...");
  const [details, setDetails] = useState("");

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const state = searchParams.get("state");
        const sellingPartnerId = searchParams.get("selling_partner_id");
        const code = searchParams.get("spapi_oauth_code");

        if (!state || !sellingPartnerId || !code) {
          setStatus("error");
          setMessage("Missing required authorization parameters");
          setDetails("The authorization URL is missing required parameters. Please try again.");
          return;
        }

        const response = await unauthenticatedAxios.get("/amazon-seller/callback", {
          params: {
            code: code,
            state: state,
            selling_partner_id: sellingPartnerId
          }
        });

        const result = response.data;

        if (result.success) {
          setStatus("success");
          setMessage(result.message);          
          toast.success("Amazon account connected successfully!");
          
          setTimeout(() => {
            navigate("/settings");
          }, 3000);
        } else {
          setStatus("error");
          setMessage(result.message);
          setDetails(result.error || "An unexpected error occurred during authorization.");
          toast.error("Failed to connect Amazon account");
        }
      } catch (error) {
        console.error("Callback error:", error);
        setStatus("error");
        
        if (error.response && error.response.data) {
          setMessage("Failed to connect Amazon account");
          setDetails(error.response.data.error || error.response.data.message || "An unexpected error occurred during authorization.");
        } else {
          setMessage("Connection error");
          setDetails("Unable to connect to the server. Please check your internet connection and try again.");
        }
        
        toast.error("Failed to connect Amazon account");
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  const getStatusIcon = () => {
    switch (status) {
      case "authenticating":
        return <FaSpinner className="animate-spin text-blue-500" size={48} />;
      case "success":
        return <FaCheckCircle className="text-green-500" size={48} />;
      case "error":
        return <FaTimesCircle className="text-red-500" size={48} />;
      default:
        return <FaSpinner className="animate-spin text-blue-500" size={48} />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case "authenticating":
        return "border-blue-200 bg-blue-50";
      case "success":
        return "border-green-200 bg-green-50";
      case "error":
        return "border-red-200 bg-red-50";
      default:
        return "border-gray-200 bg-gray-50";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className={`rounded-2xl border-2 ${getStatusColor()} p-8 shadow-xl backdrop-blur-sm`}>
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <FaAmazon className="text-orange-500 mr-3" size={32} />
              <h1 className="text-2xl font-bold text-gray-800">Amazon Seller App</h1>
            </div>
            <h2 className="text-lg font-semibold text-gray-700">Authorization Status</h2>
          </div>

          <div className="flex justify-center mb-6">
            {getStatusIcon()}
          </div>

          <div className="text-center mb-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-2">{message}</h3>
            {details && (
              <p className="text-gray-600 text-sm leading-relaxed">{details}</p>
            )}
          </div>

          <div className="space-y-3">
            {status === "success" && (
              <div className="text-center">
                <div className="inline-flex items-center text-sm text-gray-500 mb-4">
                  <FaSpinner className="animate-spin mr-2" size={14} />
                  Redirecting to settings in 3 seconds...
                </div>
                <button
                  onClick={() => navigate("/settings")}
                  className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center"
                >
                  Go to Settings Now
                  <HiOutlineExternalLink className="ml-2" size={16} />
                </button>
              </div>
            )}

            {status === "error" && (
              <div className="space-y-3">
                <button
                  onClick={() => navigate("/settings")}
                  className="w-full bg-gray-500 hover:bg-gray-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center"
                >
                  Back to Settings
                  <HiOutlineExternalLink className="ml-2" size={16} />
                </button>
              </div>
            )}

            {status === "authenticating" && (
              <div className="text-center">
                <p className="text-sm text-gray-500">Please wait while we process your authorization...</p>
              </div>
            )}
          </div>    
        </div>

        <div className="text-center mt-6">
          <p className="text-sm text-gray-500">
            Secure connection powered by Amazon Seller Partner API
          </p>
        </div>
      </div>
    </div>
  );
};

export default AmazonCallback;
